import React, { useContext, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { TextileAppState } from 'api/textile_deals/fetchTextileAppState';
import TopActionBlock from 'blocks/TopActionBlock';
import Divider from 'components/Divider/Divider';
import GenericErrorComponent from 'components/InfoBoxComponent/GenericErrorComponent';
import LoadingIndicatorComponent from 'components/LoadingIndicatorComponent';
import PageHeader from 'components/PageHeader/PageHeader';
import PageStackBlock from 'components/PageStackBlock/PageStackBlock';
import Paragraph from 'components/Paragraph/Paragraph';
import { HasAccessContext } from 'contexts/HasAccessContext';
import BackLink from 'domain/Links/BackLink';
import InputMessage from 'elements/InputMessage';
import { useTextileGroupsQuery } from 'queries/textile_deals/useTextileGroupsQueries';
import {
  useDeleteTexitleNameQuery,
  useTextileNameQuery,
  useUpdateTextileNameQuery
} from 'queries/textile_deals/useTextileNamesQueries';
import { useTextileOrderForManagementQuery } from 'queries/textile_deals/useTextileOrderQueries';
import { FormattedMessage } from 'react-intl';
import { HistoryProps, idFromMatch } from 'utils/history';
import { textileOrderIdFromMatch } from 'utils/history/textile-order-id-from-match';
import { textileOrderRoute } from 'utils/history/textile-order-route';
import useForm, { FormType, handleFormError } from 'utils/hooks/useForm';
import isTextileLocked from 'utils/isTextileLocked';
import TextileNameForm from './TextileNameForm';

interface FormProps {
  form: FormType;
}

const EditVirtualTextileName: React.FC<HistoryProps & FormProps> = (props) => {
  const textileOrderId = textileOrderIdFromMatch(props.match);
  const id = idFromMatch(props.match);
  const queryClient = useQueryClient();
  const textileAppState = queryClient.getQueryData<TextileAppState>([
    'textile_app_state',
    textileOrderId
  ]);
  const accessContext = useContext(HasAccessContext);
  const textileName = useTextileNameQuery(textileOrderId, id);
  const textileGroups = useTextileGroupsQuery(textileOrderId);
  const textileOrder = useTextileOrderForManagementQuery(textileOrderId);
  const updateName = useUpdateTextileNameQuery(textileOrderId);
  const removeName = useDeleteTexitleNameQuery(textileOrderId);

  if (!accessContext) {
    throw Error('Component must be used within HasAccessContextProvider');
  }

  useEffect(() => {
    const data = textileName.data;
    const textileOrder = textileAppState?.textile_order;
    const groups = textileGroups.data;

    if (data && groups && textileOrder) {
      props.form.setField('first_name', data.first_name || '');
      props.form.setField('last_name', data.last_name || '');
      if (textileOrder?.show_groups) {
        props.form.setField(
          'textile_group_id',
          data.textile_group?.id.toString() || groups[0]?.id.toString() || ''
        );
      }
    }

    accessContext.checkSectionAccess('management_checkout');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessContext,
    textileName.data,
    textileAppState?.textile_order,
    textileGroups.data
  ]);

  const renderPage = (content?: React.ReactNode) => {
    if (!textileAppState) {
      return null;
    }

    return (
      <>
        <TopActionBlock>
          <BackLink
            to={
              isTextileLocked(
                textileAppState.textile_order.order_state,
                'atLeastOrderStarted'
              )
                ? textileOrderRoute(props.match, '/checkout')
                : textileOrderRoute(props.match, '/management')
            }
          />
        </TopActionBlock>

        <PageStackBlock>{content}</PageStackBlock>
      </>
    );
  };

  if (textileName.isError) {
    return renderPage(
      <GenericErrorComponent onRetryClick={() => textileName.refetch()} />
    );
  }

  if (textileGroups.isError) {
    return renderPage(
      <GenericErrorComponent onRetryClick={() => textileGroups.refetch()} />
    );
  }

  if (textileOrder.isError) {
    return renderPage(
      <GenericErrorComponent onRetryClick={() => textileOrder.refetch()} />
    );
  }

  if (
    !textileAppState ||
    props.form.loading ||
    textileName.isLoading ||
    !textileName.data ||
    textileGroups.isLoading ||
    !textileGroups.data ||
    textileOrder.isLoading ||
    !textileOrder.data
  ) {
    return renderPage(<LoadingIndicatorComponent />);
  }

  const save = () => {
    const { form, match } = props;

    if (!id || !textileOrderId) {
      return;
    }

    form.setLoading(true);
    form.resetErrors();

    updateName.mutate(
      { data: { ...form.values }, id, textileOrderId },
      {
        onSuccess: () => {
          props.history.push(
            isTextileLocked(
              textileAppState.textile_order.order_state,
              'atLeastOrderStarted'
            )
              ? textileOrderRoute(match, '/checkout')
              : textileOrderRoute(match, '/management')
          );
        },
        onError: (error) => {
          form.setLoading(false);
          if (!handleFormError(form, error)) {
            form.setError('base', true);
          }
        }
      }
    );
  };

  const deleteName = () => {
    const { form } = props;

    if (!id || !textileOrderId) {
      return;
    }

    form.setLoading(true);
    form.resetErrors();

    removeName.mutate(
      { id, textileOrderId },
      {
        onSuccess: () => {
          props.history.push(textileOrderRoute(props.match, '/management'));
        },
        onError: (error) => {
          form.setLoading(false);
          handleFormError(form, error);

          return;
        }
      }
    );
  };

  const renderForm = () => {
    const { form } = props;

    const groups = textileGroups.data;
    const orderData = textileOrder.data;
    const showGroups = textileAppState.textile_order.show_groups;
    return renderPage(
      <>
        <PageHeader
          headline={<FormattedMessage id="textile virtual student" />}
          text=""
        />

        {orderData.organizer_print_data_message && (
          <InputMessage info={true}>
            <Paragraph weight="BOLD" size="L">
              <FormattedMessage id="message from organiser" />
            </Paragraph>

            <Divider color="PURPLE" />

            <Paragraph>{orderData.organizer_print_data_message}</Paragraph>
          </InputMessage>
        )}

        <TextileNameForm
          form={form}
          onSave={() => save()}
          onDelete={() => deleteName()}
          textileGroups={groups}
          showGroups={showGroups}
        />
      </>
    );
  };

  return renderForm();
};

export default (props: HistoryProps) => {
  const form = useForm();
  // @ts-ignore
  return <EditVirtualTextileName {...props} form={form} />;
};
